/* ==========================================================================
   Sport Zone — "Open now" badge for the Visit us section.

   Reads the same schema.org opening-hours rules that feed the LocalBusiness
   markup (Mo-Sa 09:00-19:00, one per line) and works out whether the shop is
   open right now in the STORE's timezone, not the visitor's — otherwise
   anyone browsing from abroad is told the wrong thing.

   Progressive enhancement: the badge starts hidden and only appears once a
   rule has been parsed successfully.
   ========================================================================== */

(function () {
  'use strict';

  var CODES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  var MINUTES_PER_DAY = 1440;

  function normaliseCode(value) {
    var text = value.trim();
    return text.charAt(0).toUpperCase() + text.slice(1, 2).toLowerCase();
  }

  function expandDays(spec) {
    var days = [];

    spec.split(',').forEach(function (chunk) {
      var range = chunk.trim().split('-');

      if (range.length === 2) {
        var from = CODES.indexOf(normaliseCode(range[0]));
        var to = CODES.indexOf(normaliseCode(range[1]));
        if (from < 0 || to < 0) return;
        /* Ranges may wrap the week, e.g. Fr-Mo. */
        for (var day = from; ; day = (day + 1) % 7) {
          days.push(day);
          if (day === to) break;
        }
      } else {
        var single = CODES.indexOf(normaliseCode(range[0]));
        if (single >= 0) days.push(single);
      }
    });

    return days;
  }

  function parseRules(text) {
    return text
      .split('\n')
      .map(function (line) {
        var match = line
          .trim()
          .match(/^([A-Za-z,\- ]+?)\s+(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
        if (!match) return null;

        var days = expandDays(match[1]);
        if (!days.length) return null;

        return {
          days: days,
          start: parseInt(match[2], 10) * 60 + parseInt(match[3], 10),
          end: parseInt(match[4], 10) * 60 + parseInt(match[5], 10),
        };
      })
      .filter(Boolean);
  }

  function nowInZone(timeZone) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(new Date());
    } catch (error) {
      return null;
    }

    var found = {};
    parts.forEach(function (part) {
      found[part.type] = part.value;
    });

    var day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(
      found.weekday
    );
    if (day < 0) return null;

    /* Some locales render midnight as 24. */
    var hour = parseInt(found.hour, 10) % 24;
    return { day: day, minutes: hour * 60 + parseInt(found.minute, 10) };
  }

  function formatTime(minutes) {
    var hour = Math.floor(minutes / 60);
    var minute = minutes % 60;
    return (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute;
  }

  function findOpenRule(rules, now) {
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (rule.days.indexOf(now.day) < 0) continue;
      if (now.minutes >= rule.start && now.minutes < rule.end) return rule;
    }
    return null;
  }

  /* Nearest opening within the next week, as {day, start, offset}. */
  function findNextOpening(rules, now) {
    for (var offset = 0; offset < 8; offset++) {
      var day = (now.day + offset) % 7;
      var best = null;

      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];
        if (rule.days.indexOf(day) < 0) continue;
        if (offset === 0 && rule.start <= now.minutes) continue;
        if (!best || rule.start < best.start) best = rule;
      }

      if (best) return { day: day, start: best.start, offset: offset };
    }
    return null;
  }

  function render(el, isOpen, label, detail) {
    el.textContent = '';
    el.dataset.open = isOpen ? 'true' : 'false';

    var main = document.createElement('span');
    main.textContent = label;
    el.appendChild(main);

    if (detail) {
      var extra = document.createElement('span');
      extra.className = 'sz-visit__status-detail';
      extra.textContent = detail;
      el.appendChild(extra);
    }

    el.hidden = false;
  }

  function init(el) {
    if (el.dataset.szVisitReady === 'true') return;
    el.dataset.szVisitReady = 'true';

    var rules = parseRules(el.dataset.szVisitHours || '');
    if (!rules.length) return;

    var now = nowInZone(el.dataset.szVisitTz || 'Asia/Beirut');
    if (!now) return;

    var openLabel = el.dataset.szVisitOpenLabel || 'Open now';
    var closedLabel = el.dataset.szVisitClosedLabel || 'Closed';

    var openRule = findOpenRule(rules, now);

    if (openRule) {
      var minutesLeft = openRule.end - now.minutes;
      var detail = 'closes ' + formatTime(openRule.end % MINUTES_PER_DAY);
      /* Inside the last hour, minutes are more useful than a clock time. */
      if (minutesLeft <= 60) detail = 'closes in ' + minutesLeft + ' min';
      render(el, true, openLabel, detail);
      return;
    }

    var next = findNextOpening(rules, now);
    if (!next) {
      render(el, false, closedLabel, null);
      return;
    }

    var when =
      next.offset === 0
        ? 'opens ' + formatTime(next.start)
        : 'opens ' + NAMES[next.day] + ' ' + formatTime(next.start);

    render(el, false, closedLabel, when);
  }

  function boot() {
    var badges = document.querySelectorAll('[data-sz-visit-status]');
    Array.prototype.forEach.call(badges, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
